import {
  confirmDatasetConfigurationCandidate,
  getDatasetConfiguration,
  getReservedKeyContracts,
  isRevisionConflict,
  isUserProposal,
  proposeDatasetConfiguration,
  putDatasetConfiguration,
  type ConfigCandidateView,
  type DatasetConfigurationPair,
  type DatasetConfigurationPermissions,
  type DatasetConfigurationPutReq,
  type ReservedKeyContract,
  type UnresolvedReference,
} from "@/api";
import { toast } from "@carbon6/c6-frontend-design";
import type { Lifecycle } from "@/lifecycle";
import { resolveErrorMessage } from "../datasetFormatters";
import {
  EMPTY_DRAFT_CONFIGURATION,
  draftFromConfiguration,
  findDuplicateLabels,
  isDirty,
  toPutPayload,
  withEmbodiment,
  withLabelAdded,
  withLabelRemoved,
  withLabelsForKey,
  withTip,
  type DraftConfiguration,
  type DraftEmbodiment,
  type DraftLabel,
  type DraftTip,
} from "./datasetConfigurationDraft";

/**
 * 데이터셋 리뷰 컨트롤러(외부 스토어 + gateway seam).
 *
 * baseline pair(GET /configuration)와 그 위의 편집 draft·revision 을 소유한다.
 * draft 편집은 서버를 호출하지 않고 로컬에만 반영한다. 제출 하나가 full target 을 보낸다.
 * - 상세: 검토 요청(propose) 또는 저장(save).
 * - 리뷰: 확정(confirm) 또는 직전 값으로 되돌리기(revert).
 * 어느 제출을 허용할지는 응답의 `permissions` 가 정한다. 409 revision_conflict 면 최신 pair 를
 * 다시 받아 비교 mode(conflict)로 전환한다. 자동 재시도와 병합은 하지 않는다.
 *
 * Owns:
 * - baseline·draft·revision·contracts 스냅샷과 파생 값(dirty·중복).
 * - 제출 경계, 409 를 받으면 비교 mode 로 진입, gateway seam.
 *
 * Does not own:
 * - draft 전이 규칙(순수 코어 datasetConfigurationDraft).
 * - 윈도우 lifecycle, dirty 이탈 가드, 렌더.
 */

export type DatasetReviewLoadState = "loading" | "loaded" | "error";

// 409 revision_conflict 뒤에 다시 조회한 최신 pair다. 비교 화면의 소스로 쓴다. null이면 충돌이 없다.
export type DatasetReviewConflict = {
  latestPair: DatasetConfigurationPair;
};

export type DatasetReviewSnapshot = {
  loadState: DatasetReviewLoadState;
  loadError: string | null;
  draft: DraftConfiguration;
  // 마지막으로 서버와 동기화한 configuration(active)의 draft. dirty 를 비교하는 기준이다.
  baselineDraft: DraftConfiguration;
  revision: number;
  contracts: readonly ReservedKeyContract[];
  busy: boolean;
  dirty: boolean;
  duplicateLabels: readonly DraftLabel[];
  conflict: DatasetReviewConflict | null;
  // GET 시점 카탈로그에서 삭제된 참조. 화면에 누락으로 표시한다. baseline pair 기준이다.
  unresolvedReferences: readonly UnresolvedReference[];
  // Dataset 당 하나인 후보(있으면). 사람이 보낸 검토 요청이거나 저장이 남긴 직전 값이다.
  // 검토 요청만 draft seed 가 된다. 직전 값은 되돌리기에만 쓴다. 서버도 같은 이름을 쓴다
  // (dataset_configuration_candidates).
  candidate: ConfigCandidateView | null;
  // 요청자 기준 쓰기 자격. 로드 전에는 null 이라서 어떤 제출도 허용하지 않는다.
  permissions: DatasetConfigurationPermissions | null;
};

const INITIAL_SNAPSHOT: DatasetReviewSnapshot = {
  loadState: "loading",
  loadError: null,
  draft: EMPTY_DRAFT_CONFIGURATION,
  baselineDraft: EMPTY_DRAFT_CONFIGURATION,
  revision: 0,
  contracts: [],
  busy: false,
  dirty: false,
  duplicateLabels: [],
  conflict: null,
  unresolvedReferences: [],
  candidate: null,
  permissions: null,
};

// api 경계 seam. 기본 구현은 실제 api 를 주입하고, 테스트는 fake 를 주입한다.
export type DatasetReviewGateway = {
  load: (datasetUuid: string) => Promise<{
    pair: DatasetConfigurationPair;
    contracts: readonly ReservedKeyContract[];
  }>;
  // 409 뒤에 최신 pair 를 다시 조회한다. contracts 는 세션 캐시라서 다시 받지 않는다.
  reloadPair: (datasetUuid: string) => Promise<DatasetConfigurationPair>;
  // 변경 요청. active 는 그대로 두고 후보만 갱신한다.
  propose: (
    datasetUuid: string,
    payload: DatasetConfigurationPutReq,
  ) => Promise<DatasetConfigurationPair>;
  // 저장. target 을 곧바로 active 로 적용한다. 직전 active 는 previous_active 후보로 남는다.
  save: (
    datasetUuid: string,
    payload: DatasetConfigurationPutReq,
  ) => Promise<DatasetConfigurationPair>;
  // 확정. FE 가 조립한 target 을 active 로 적용하고 후보를 소비하고 리뷰 큐에서 제거한다.
  // body 는 요청과 같다.
  confirmCandidate: (
    datasetUuid: string,
    payload: DatasetConfigurationPutReq,
  ) => Promise<DatasetConfigurationPair>;
};

export const defaultDatasetReviewGateway: DatasetReviewGateway = {
  load: async (datasetUuid) => {
    const [pair, contracts] = await Promise.all([
      getDatasetConfiguration(datasetUuid),
      getReservedKeyContracts(),
    ]);
    return { pair, contracts };
  },
  reloadPair: (datasetUuid) => getDatasetConfiguration(datasetUuid),
  propose: proposeDatasetConfiguration,
  save: putDatasetConfiguration,
  confirmCandidate: confirmDatasetConfigurationCandidate,
};

export class DatasetReviewController implements Lifecycle<readonly []> {
  private readonly listeners = new Set<() => void>();
  private snapshot: DatasetReviewSnapshot = INITIAL_SNAPSHOT;
  private disposed = true;
  private lifecycleGeneration = 0;

  constructor(
    private readonly datasetUuid: string,
    private readonly gateway: DatasetReviewGateway = defaultDatasetReviewGateway,
    // direct 는 상세에서 편집해 검토 요청하거나 저장한다.
    // candidate 는 리뷰 큐에서 편집해 확정하거나 되돌린다.
    // 두 모드 모두 받은 검토 요청이 있으면 그 요청을 draft seed 로 쓴다(없으면 active).
    private readonly mode: "direct" | "candidate" = "direct",
  ) {}

  getSnapshot = (): DatasetReviewSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // 제출 공통 게이트: 로드 완료 + 중복 없음 + 비busy + 충돌 아님. 예약 라벨 개수는 게이트가
  // 아니다. 확정 configuration 에서 예약 라벨은 optional 이고, 개수를 묻는 곳은 업로드뿐이다.
  private get canSubmit(): boolean {
    const s = this.snapshot;
    return (
      s.loadState === "loaded" &&
      s.draft.displayName.trim().length > 0 &&
      s.draft.displayName.trim().length <= 255 &&
      !s.busy &&
      s.conflict === null &&
      s.duplicateLabels.length === 0
    );
  }

  private get canWrite(): boolean {
    return this.snapshot.permissions?.can_write === true;
  }

  // 변경 요청 가능 여부. 보낼 편집 내용이 있어야 한다. 적용된 값과 같은 값을 다시 보내는 것은
  // 요청이 아니다.
  get canPropose(): boolean {
    return this.canSubmit && this.snapshot.permissions?.can_propose === true && this.snapshot.dirty;
  }

  // 저장 가능 여부. 적용된 값과 달라야 한다. dirty 플래그가 아니라 draft 와 active 를 직접
  // 비교한다. 상세는 받은 검토 요청으로 폼을 채워도 dirty 를 설정하지 않는다. 그런데 생성자가
  // 그 요청을 그대로 반영하는 방법은 이 저장뿐이다(리뷰 페이지는 admin 전용).
  get canSave(): boolean {
    const s = this.snapshot;
    return this.canSubmit && this.canWrite && isDirty(s.draft, s.baselineDraft);
  }

  // 확정 가능 여부. dirty 를 검사하지 않는다. 업로드 직후처럼 고칠 것이 없어도 "이 설정이
  // 맞다" 를 확정해 큐에서 제거하는 것이 리뷰의 정상 동작이다. dirty 를 요구하면 그 데이터셋이
  // 큐에 계속 남는다.
  get canConfirm(): boolean {
    return this.canSubmit && this.canWrite;
  }

  // 직전 값으로 되돌리기 가능 여부. 저장이 남긴 후보가 있고 폼을 고치지 않았을 때만 가능하다.
  // 폼에 편집이 있으면 되돌리기가 그 편집을 경고 없이 버린다.
  get canRevert(): boolean {
    const s = this.snapshot;
    return (
      this.mode === "candidate" &&
      s.candidate?.origin === "previous_active" &&
      this.canSubmit &&
      this.canWrite &&
      !isDirty(s.draft, s.baselineDraft)
    );
  }

  init = async (): Promise<void> => {
    if (!this.disposed) {
      return;
    }
    this.disposed = false;
    const generation = (this.lifecycleGeneration += 1);
    this.patch({ loadState: "loading", loadError: null });
    try {
      const { pair, contracts } = await this.gateway.load(this.datasetUuid);
      if (!this.isActive(generation)) {
        return;
      }
      this.adoptPair(pair, contracts);
    } catch (error) {
      if (!this.isActive(generation)) {
        return;
      }
      this.patch({ loadState: "error", loadError: resolveErrorMessage(error) });
    }
  };

  // 조회 수명만 닫는다. 진행 중인 요청을 generation 으로 무효화하고 다음 init 을 기다린다.
  // **구독자는 비우지 않는다.** 구독 수명은 subscribe 가 반환한 unsubscribe 가 따로 소유한다.
  // 리뷰 탭에서 훅은 상세 controller 를 유지한 채 enabled 만 토글한다(useDatasetReview).
  // 여기서 listeners 를 비우면 탭으로 돌아온 뒤 useSyncExternalStore 가 다시 구독하지 않는다.
  // subscribe 함수가 같기 때문이다. 그러면 이후 모든 patch 가 화면에 도달하지 못한다.
  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.lifecycleGeneration += 1;
  }

  // ── draft 편집(로컬, 서버 호출 없음) ──

  setMetadata(
    changes: Partial<
      Pick<DraftConfiguration, "displayName" | "operatorMember" | "dataOrigin" | "recordingMode">
    >,
  ): void {
    const draft = { ...this.snapshot.draft, ...changes };
    if (changes.recordingMode && !changes.recordingMode.modified_manually) {
      const baseline = this.snapshot.baselineDraft.recordingMode;
      draft.recordingMode = {
        modified_manually: false,
        value: baseline.modified_manually ? null : baseline.value,
      };
    }
    this.setDraft(draft);
  }

  setEmbodiment(embodiment: DraftEmbodiment): void {
    this.setDraft(withEmbodiment(this.snapshot.draft, embodiment));
  }

  setTip(tip: DraftTip): void {
    this.setDraft(withTip(this.snapshot.draft, tip));
  }

  // reserved key(env/source/task) 편집. 그 key 의 라벨 전체를 next 로 치환한다.
  setLabelsForKey(key: string, next: readonly DraftLabel[]): void {
    this.setDraft(withLabelsForKey(this.snapshot.draft, key, next));
  }

  addLabel(label: DraftLabel): void {
    this.setDraft(withLabelAdded(this.snapshot.draft, label));
  }

  removeLabel(label: DraftLabel): void {
    this.setDraft(withLabelRemoved(this.snapshot.draft, label));
  }

  // 편집 취소. draft 를 baseline 으로 되돌린다.
  resetDraft(): void {
    this.setDraft(this.snapshot.baselineDraft);
  }

  // 변경 요청(direct 모드). 적용된 설정은 그대로 두고 후보만 갱신한다.
  // - 성공하면 최신 pair 를 baseline 으로 채택하고 true 를 반환한다.
  // - 409 revision_conflict 면 비교 mode(conflict)로 진입하고 false 를 반환한다.
  // - 그 외 오류는 토스트를 표시하고 false 를 반환한다.
  // 자동 재시도와 병합은 하지 않는다.
  propose = (): Promise<boolean> => {
    if (!this.canPropose) {
      return Promise.resolve(false);
    }
    return this.submit(
      (payload) => this.gateway.propose(this.datasetUuid, payload),
      "검토 요청을 올렸습니다. 리뷰에서 확정하면 반영됩니다.",
    );
  };

  // 저장(direct 모드, 생성자·admin). draft 를 곧바로 active 로 반영한다. 서버가 직전 active 를
  // previous_active 후보로 남기므로 응답 pair 에는 후보가 있다. 이 후보는 요청이 아니므로
  // 폼 seed 로 쓰지 않는다.
  save = (): Promise<boolean> => {
    if (!this.canSave) {
      return Promise.resolve(false);
    }
    return this.submit(
      (payload) => this.gateway.save(this.datasetUuid, payload),
      "Dataset 설정을 저장했습니다.",
    );
  };

  // 확정(candidate 모드). FE draft 를 full target 으로 보내 active 에 적용하고 후보를 소비한다.
  // 서버가 재검증·적용·삭제·리뷰 큐 제거를 한 트랜잭션으로 처리한다.
  confirmCandidate = (): Promise<boolean> => {
    if (!this.canConfirm) {
      return Promise.resolve(false);
    }
    return this.submit(
      (payload) => this.gateway.confirmCandidate(this.datasetUuid, payload),
      "Dataset 설정을 확정했습니다. 리뷰 큐에서 내려갑니다.",
    );
  };

  // 직전 값으로 되돌리기(candidate 모드). 저장이 남긴 후보를 target 으로 확정한다.
  // 확정은 target 적용·후보 소비·리뷰 큐 제거를 한 트랜잭션으로 처리한다. 그래서 결과는
  // "직전 값을 다시 적용하고 검토 완료로 큐에서 제거" 와 같다. 별도 엔드포인트는 없다.
  revertToPrevious = (): Promise<boolean> => {
    const candidate = this.snapshot.candidate;
    if (!this.canRevert || candidate === null) {
      return Promise.resolve(false);
    }
    return this.submit(
      (payload) => this.gateway.confirmCandidate(this.datasetUuid, payload),
      "직전 설정으로 되돌렸습니다. 리뷰 큐에서 내려갑니다.",
      draftFromConfiguration(candidate.configuration),
    );
  };

  // 제출 공용 경로. draft(또는 되돌리기처럼 지정한 target)를 full target 으로 조립해 보낸다.
  // 성공하면 응답 pair 를 새 baseline 으로 채택한다. 409 면 비교 mode 로 전환한다.
  private async submit(
    action: (payload: DatasetConfigurationPutReq) => Promise<DatasetConfigurationPair>,
    successMessage: string,
    target: DraftConfiguration = this.snapshot.draft,
  ): Promise<boolean> {
    const generation = this.lifecycleGeneration;
    const payload = toPutPayload(target, this.snapshot.revision);
    this.patch({ busy: true });
    try {
      const pair = await action(payload);
      if (!this.isActive(generation)) {
        return false;
      }
      this.adoptPair(pair, this.snapshot.contracts);
      toast.success(successMessage);
      return true;
    } catch (error) {
      if (!this.isActive(generation)) {
        return false;
      }
      return await this.handleSaveError(error, generation);
    } finally {
      if (this.isActive(generation)) {
        this.patch({ busy: false });
      }
    }
  }

  // 비교 mode 에서 사용자가 자기 draft 를 폐기하고 최신 서버 값으로 다시 초기화한다(충돌 해소).
  adoptLatestFromConflict(): void {
    const conflict = this.snapshot.conflict;
    if (conflict === null) {
      return;
    }
    this.adoptPair(conflict.latestPair, this.snapshot.contracts);
  }

  private async handleSaveError(error: unknown, generation: number): Promise<boolean> {
    if (isRevisionConflict(error)) {
      // 409 응답은 최신 pair 를 포함하지 않는다. 그래서 다시 받아 비교 mode 로 전환한다.
      try {
        const latestPair = await this.gateway.reloadPair(this.datasetUuid);
        if (this.isActive(generation)) {
          this.patch({ conflict: { latestPair } });
          toast.error("다른 사용자가 먼저 저장했습니다. 변경 내용을 비교해 주세요.");
        }
      } catch {
        if (this.isActive(generation)) {
          toast.error("최신 설정을 다시 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
        }
      }
      return false;
    }
    toast.error(resolveErrorMessage(error));
    return false;
  }

  private setDraft(draft: DraftConfiguration): void {
    this.patch({
      draft,
      dirty: isDirty(draft, this.snapshot.baselineDraft),
      duplicateLabels: findDuplicateLabels(draft),
    });
  }

  // 서버 pair 를 새 baseline 으로 채택하고 draft 를 그 값으로 리셋한다. 받은 검토 요청이 있으면
  // 두 모드 모두 draft 를 그 요청으로 seed 한다. 상세에서 active 부터 다시 채우면 다른 사람이
  // 보낸 요청을 모르고 덮어쓴다. baseline 은 active 로 두어 "무엇이 바뀌는지"의 기준을 유지한다.
  //
  // 저장이 남긴 직전 값(previous_active)은 seed 하지 않는다. 그 값으로 채우면 방금 저장한 폼이
  // 이전 값으로 돌아간다. 그러면 리뷰의 확정이 그 저장을 되돌린다.
  //
  // dirty 는 리뷰에서만 함께 설정한다. 리뷰에서는 받은 요청을 그대로 확정하는 것이 정상 동작이라
  // 버튼이 활성이어야 한다. 상세에서는 아무것도 고치지 않았을 때 이탈 가드가 동작하거나 같은
  // 요청을 다시 보낼 수 있으면 안 된다.
  private adoptPair(
    pair: DatasetConfigurationPair,
    contracts: readonly ReservedKeyContract[],
  ): void {
    const baselineDraft = draftFromConfiguration(pair.active);
    const seedFromCandidate = isUserProposal(pair.candidate);
    const draft = isUserProposal(pair.candidate)
      ? draftFromConfiguration(pair.candidate.configuration)
      : baselineDraft;
    this.patch({
      loadState: "loaded",
      loadError: null,
      draft,
      baselineDraft,
      revision: pair.revision,
      contracts,
      dirty: seedFromCandidate && this.mode === "candidate" ? isDirty(draft, baselineDraft) : false,
      duplicateLabels: findDuplicateLabels(draft),
      conflict: null,
      unresolvedReferences: pair.unresolved_references,
      candidate: pair.candidate,
      permissions: pair.permissions,
    });
  }

  private isActive(generation: number): boolean {
    return !this.disposed && generation === this.lifecycleGeneration;
  }

  private patch(patch: Partial<DatasetReviewSnapshot>): void {
    if (this.disposed) {
      return;
    }
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
